import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import MaterialIcon from '../../components/MaterialIcon';
import { roleRestrictions } from '../../constants';
import { Store } from '../../reducers';
import { userRoleAboveRequiredLevel } from '../authorization/userRoleAboveRequiredLevel';
import styles from './DeviceEditNameHeader.module.scss';

type Props = {
    name: string;
    renameSegment: (name: string) => void;
};

export const DeviceEditNameHeaderComponent = ({ renameSegment, name }: Props): React.ReactElement => {
    const { t: txt } = useTranslation();
    const { selectedGroup } = useSelector((store: Store) => store.userSettings);
    const userRole = selectedGroup && selectedGroup.role;
    const [isEditing, setIsEditing] = useState(false);
    const [updatedName, setUpdatedName] = useState(name);

    useEffect(() => {
        if (!isEditing) {
            setUpdatedName(name);
        }
    }, [name]);

    useEffect(() => {
        if (isEditing) {
            const editComponent = document.getElementById('editName');
            if (editComponent) editComponent.focus();
        }
    }, [isEditing]);

    const updateName = (newName: string): void => {
        let deviceName = updatedName;
        if (newName.length > 0 && newName !== deviceName) {
            deviceName = newName;
            renameSegment(newName);
        }
        setIsEditing(false);
        setUpdatedName(deviceName);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        e.preventDefault();
        if (e.key === 'Enter') {
            updateName(e.currentTarget.value.trim());
        }
    };

    const onBlur = (e: React.SyntheticEvent<HTMLInputElement>): void => {
        e.preventDefault();
        updateName(e.currentTarget.value.trim());
    };

    const allowedToEdit = (): boolean => {
        return !userRole || userRoleAboveRequiredLevel(userRole, roleRestrictions.editDeviceOrBuilding);
    };

    const editName = (): void => {
        if (allowedToEdit()) {
            setIsEditing(true);
        }
    };

    return (
        <div>
            {isEditing && (
                <div className={styles.editContainer}>
                    <input
                        className={styles.editInput}
                        type="text"
                        id="editName"
                        required
                        onBlur={onBlur}
                        onKeyUp={onKeyDown}
                        autoComplete="false"
                        defaultValue={updatedName}
                        maxLength={50}
                        data-testid="edit-name-input"
                    />
                </div>
            )}
            {!isEditing &&
                (allowedToEdit() ? (
                    <button
                        type="button"
                        title={txt('EditDeviceName')}
                        onClick={editName}
                        className={styles.editButton}
                        data-testid="edit-name-button"
                    >
                        <div className={styles.editContent}>
                            <h1 className={styles.headerText}>{updatedName}</h1>
                            <MaterialIcon extraClass={styles.icon} name="edit" />
                        </div>
                    </button>
                ) : (
                    <div className={styles.editContent}>
                        <h1 className={styles.headerText}>{updatedName}</h1>
                    </div>
                ))}
        </div>
    );
};

export default DeviceEditNameHeaderComponent;
